import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  CardHeader,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  Build as WorkshopIcon,
  ShoppingCart as SalesIcon
} from '@mui/icons-material';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { pl } from 'date-fns/locale';
import { db } from '../../../firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

const GeneralStats = ({ data, selectedMonth }) => {
  const { products, sales, orders, workshop } = data;
  const [previousMonthData, setPreviousMonthData] = useState({
    sales: [],
    orders: []
  });
  const [trends, setTrends] = useState({
    salesTrend: 0,
    ordersTrend: 0
  });
  
  // Obliczanie statystyk
  const totalProducts = products.length;
  const availableProducts = products.filter(p => p.status === 'available').length;
  const reservedProducts = products.filter(p => p.status === 'reserved').length;
  const workshopItems = workshop.filter(item => item.status !== 'completed').length;
  
  const totalSales = sales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(order => ['new', 'in_progress'].includes(order.status)).length;
  const completedOrders = orders.filter(order => order.status === 'completed').length;
  
  // Pobieranie danych z poprzedniego miesiąca do obliczenia trendów
  useEffect(() => {
    const fetchPreviousMonthData = async () => {
      try {
        // Określenie zakresu dat dla poprzedniego miesiąca
        const previousMonth = subMonths(selectedMonth, 1);
        const startDate = startOfMonth(previousMonth);
        const endDate = endOfMonth(previousMonth);
        
        // Pobieranie danych sprzedaży z poprzedniego miesiąca
        const previousSalesQuery = query(
          collection(db, 'sales'),
          where('status', '==', 'completed'),
          where('completedAt', '>=', startDate),
          where('completedAt', '<=', endDate)
        );
        
        const previousSalesSnapshot = await getDocs(previousSalesQuery);
        const previousSalesData = previousSalesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        // Pobieranie danych zamówień z poprzedniego miesiąca
        const previousOrdersQuery = query(
          collection(db, 'orders'),
          where('created_at', '>=', Timestamp.fromDate(startDate)),
          where('created_at', '<=', Timestamp.fromDate(endDate))
        );
        
        const previousOrdersSnapshot = await getDocs(previousOrdersQuery);
        const previousOrdersData = previousOrdersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          created_at: doc.data().created_at?.toDate() || new Date()
        }));
        
        setPreviousMonthData({
          sales: previousSalesData,
          orders: previousOrdersData
        });
        
        // Obliczanie trendów
        calculateTrends(previousSalesData, previousOrdersData);
      } catch (error) {
        console.error('Błąd podczas pobierania danych z poprzedniego miesiąca:', error);
      }
    };
    
    fetchPreviousMonthData();
  }, [selectedMonth]);
  
  // Funkcja obliczająca trendy
  const calculateTrends = (previousSales, previousOrders) => {
    // Obliczanie trendu sprzedaży
    const previousTotalSales = previousSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
    let salesTrend = 0;
    
    if (previousTotalSales > 0) {
      salesTrend = Math.round(((totalSales - previousTotalSales) / previousTotalSales) * 100);
    } else if (totalSales > 0) {
      salesTrend = 100; // Jeśli w poprzednim miesiącu nie było sprzedaży, a teraz jest
    }
    
    // Obliczanie trendu zamówień
    const previousTotalOrders = previousOrders.length;
    let ordersTrend = 0;
    
    if (previousTotalOrders > 0) {
      ordersTrend = Math.round(((totalOrders - previousTotalOrders) / previousTotalOrders) * 100);
    } else if (totalOrders > 0) {
      ordersTrend = 100; // Jeśli w poprzednim miesiącu nie było zamówień, a teraz są
    }
    
    setTrends({ salesTrend, ordersTrend });
  };
  
  // Pobieranie wartości trendów
  const { salesTrend, ordersTrend } = trends;
  
  // Formatowanie wartości
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2
    }).format(value);
  };
  
  // Komponent karty statystycznej
  const StatCard = ({ title, value, icon, trend, color, suffix }) => {
    return (
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardHeader
          avatar={
            <Box sx={{ 
              backgroundColor: `${color}.light`, 
              color: `${color}.main`, 
              borderRadius: '50%', 
              p: 1, 
              display: 'flex' 
            }}>
              {icon}
            </Box>
          }
          title={title}
          titleTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
        />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="h4" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
              {value}
            </Typography>
            {suffix && (
              <Typography variant="body2" color="text.secondary">
                {suffix}
              </Typography>
            )}
          </Box>
          
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {trend > 0 ? (
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: '1rem', mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ color: 'error.main', fontSize: '1rem', mr: 0.5 }} />
              )}
              <Typography 
                variant="body2" 
                color={trend > 0 ? 'success.main' : 'error.main'}
              >
                {trend > 0 ? '+' : ''}{trend}% w porównaniu z poprzednim miesiącem
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Statystyki ogólne - {format(selectedMonth, 'LLLL yyyy', { locale: pl })}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Obrót miesięczny" 
            value={formatCurrency(totalSales)}
            icon={<MoneyIcon />}
            trend={salesTrend}
            color="success"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Liczba produktów na zamówienie" 
            value={totalOrders}
            icon={<SalesIcon />}
            trend={ordersTrend}
            color="info"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Produkty w magazynie" 
            value={availableProducts}
            icon={<InventoryIcon />}
            suffix={`z ${totalProducts}`}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Produkty w pracowni" 
            value={workshopItems}
            icon={<WorkshopIcon />}
            color="warning"
          />
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Szczegółowe statystyki
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Status produktów</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Dostępne</Typography>
                  <Typography variant="body2">{availableProducts} z {totalProducts}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={totalProducts > 0 ? (availableProducts / totalProducts) * 100 : 0} 
                  color="success"
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Zarezerwowane</Typography>
                  <Typography variant="body2">{reservedProducts} z {totalProducts}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={totalProducts > 0 ? (reservedProducts / totalProducts) * 100 : 0} 
                  color="warning"
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">W pracowni</Typography>
                  <Typography variant="body2">{workshopItems}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={totalProducts > 0 ? (workshopItems / totalProducts) * 100 : 0} 
                  color="info"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Status produktów na zamówienie</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Nowe/W realizacji</Typography>
                  <Typography variant="body2">{pendingOrders} z {totalOrders}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(pendingOrders / (totalOrders || 1)) * 100} 
                  color="warning"
                  sx={{ mb: 1, height: 8, borderRadius: 4 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Ukończone</Typography>
                  <Typography variant="body2">{completedOrders} z {totalOrders}</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(completedOrders / (totalOrders || 1)) * 100} 
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default GeneralStats;
